import { useCallback, useEffect, useRef, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

export const useMobileLayout = (): boolean => {
	const theme = useTheme();
	return useMediaQuery(theme.breakpoints.down("sm"), {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});
};

export function useResizeListener<ElementType extends HTMLElement>(
	onResize: (elem: ElementType) => void,
): (elem?: ElementType | null) => void {
	const elementRef = useRef<ElementType>();
	const resizeObserverRef = useRef<ResizeObserver>();

	const onElement = useCallback(
		(elem?: ElementType | null) => {
			if ((elem ?? undefined) !== elementRef.current) {
				resizeObserverRef.current?.disconnect();
				if (elem) {
					resizeObserverRef.current = new ResizeObserver(() => onResize(elem));
					resizeObserverRef.current.observe(elem);
					onResize(elem);
				}
				elementRef.current = elem ?? undefined;
			}
		},
		[onResize],
	);

	useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

	return onElement;
}

export const useOverflow = (): [(elem?: HTMLElement | null) => void, boolean] => {
	const [overflowing, setOverflowing] = useState(false);
	const elementRef = useRef<HTMLElement>();
	const mutationObserverRef = useRef<MutationObserver>();

	const onChange = useCallback(
		(elem: HTMLElement) => setOverflowing(elem.offsetWidth < elem.scrollWidth || elem.offsetHeight < elem.scrollHeight),
		[],
	);
	const onResize = useResizeListener(onChange);

	const onElement = useCallback(
		(elem?: HTMLElement | null) => {
			if ((elem ?? undefined) !== elementRef.current) {
				mutationObserverRef.current?.disconnect();
				if (elem) {
					mutationObserverRef.current = new MutationObserver(() => onChange(elem));
					mutationObserverRef.current.observe(elem, {
						childList: true,
						attributes: true,
						characterData: true,
						subtree: true,
					});
					onChange(elem);
				}
				elementRef.current = elem ?? undefined;
			}
			onResize(elem);
		},
		[onChange, onResize],
	);

	useEffect(() => () => mutationObserverRef.current?.disconnect(), []);

	return [onElement, overflowing];
};
